import type { ThreadAccountPostResponse } from '../api/types';
import { wallClockSortKey } from '../shared/datetimeWallClock';

export function seoulDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  return ['year', 'month', 'day'].map((key) => parts.find((p) => p.type === key)?.value).join('-');
}

/** Same publication cohort for each metric; unavailable values are not zeroes. */
function publicationCohort(accountPosts: ThreadAccountPostResponse[], days: number, now: Date) {
  const end = Date.parse(`${seoulDate(now)}T00:00:00Z`) + 86400000;
  const start = end - days * 86400000;
  return accountPosts.filter((p) => {
    const time = wallClockSortKey(p.registeredAt);
    return p.status === 'REGISTERED' && p.insights && time >= start && time < end;
  }).sort((a, b) => wallClockSortKey(a.registeredAt) - wallClockSortKey(b.registeredAt));
}

export function summarizeInsights(accountPosts: ThreadAccountPostResponse[], days: number, now = new Date()) {
  const posts = publicationCohort(accountPosts, days, now);
  const metric = (key: 'views' | 'likes' | 'replies') => posts.map((p) => p.insights?.[key]).filter((n): n is number => typeof n === 'number');
  const views = metric('views'), likes = metric('likes'), replies = metric('replies');
  const average = (values: number[]) => values.length ? Math.round(values.reduce((sum, n) => sum + n, 0) / values.length) : null;
  return { views, likes, replies, avg: posts.length ? { count: posts.length, avgViews: average(views), avgLikes: average(likes), avgReplies: average(replies) } : null };
}

export function contentRecommendations(accountPosts: ThreadAccountPostResponse[], days: number, now = new Date()) {
  const posts = publicationCohort(accountPosts, days, now).filter((p) =>
    (p.insights?.views ?? 0) > 0 && typeof p.insights?.likes === 'number' && typeof p.insights?.replies === 'number'
  );
  const rank = (key: (post: ThreadAccountPostResponse) => string | null) => {
    const groups = new Map<string, { name: string; count: number; views: number; reactions: number }>();
    posts.forEach((post) => {
      const name = key(post);
      if (!name) return;
      const group = groups.get(name) ?? { name, count: 0, views: 0, reactions: 0 };
      group.count += 1;
      group.views += post.insights!.views!;
      group.reactions += post.insights!.likes! + post.insights!.replies!;
      groups.set(name, group);
    });
    const candidates = Array.from(groups.values()).filter((group) => group.count >= 3)
      .map((group) => ({ name: group.name, count: group.count, engagementRate: group.reactions / group.views * 100 }))
      .sort((a, b) => b.engagementRate - a.engagementRate || b.count - a.count || a.name.localeCompare(b.name));
    // One group alone does not provide a comparison.
    return candidates.length >= 2 ? candidates[0] : null;
  };
  return {
    category: rank((post) => post.category?.trim() || null),
    time: rank((post) => {
      const hour = Number(post.registeredAt?.slice(11, 13));
      return Number.isFinite(hour) ? ['00–05시', '06–11시', '12–17시', '18–23시'][Math.floor(hour / 6)] : null;
    }),
  };
}
